import React, { useEffect, useState } from "react";
import { api, apiErrorMessage, formatCurrency, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, MagnifyingGlass, Users, Eye, WhatsappLogo, Coins } from "@phosphor-icons/react";
import CustomerDetailDrawer from "@/components/CustomerDetailDrawer";
import ReceivePaymentDialog from "@/components/ReceivePaymentDialog";
import { buildWhatsAppUrl, paymentReminderMessage } from "@/lib/whatsapp";

const EMPTY = { name: "", mobile: "", whatsapp: "", email: "", address: "", gst_number: "", credit_limit: 0 };

export default function Customers() {
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [drawerId, setDrawerId] = useState(null);
    const [paymentCustomerId, setPaymentCustomerId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/customers", { params: search ? { search } : {} });
            setItems(data);
        } catch (e) { toast.error(apiErrorMessage(e)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [search]);

    const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
    const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c }); setDialogOpen(true); };

    const submit = async () => {
        if (!form.name.trim()) return toast.error("Name required");
        try {
            const payload = { ...form, credit_limit: Number(form.credit_limit || 0) };
            if (editing) await api.patch(`/customers/${editing.id}`, payload);
            else await api.post("/customers", payload);
            toast.success(editing ? "Customer updated" : "Customer created");
            setDialogOpen(false); load();
        } catch (e) { toast.error(apiErrorMessage(e)); }
    };

    const sendReminder = (c) => {
        const msg = paymentReminderMessage({
            customerName: c.name,
            pendingAmount: c.outstanding_balance,
        });
        window.open(buildWhatsAppUrl(c.whatsapp || c.mobile, msg), "_blank");
    };

    return (
        <div className="space-y-6" data-testid="customers-page">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <div className="eyebrow mb-2">Contacts</div>
                    <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight font-display">Customers</h1>
                    <p className="text-sm text-neutral-500 mt-1.5">{items.length} customer{items.length !== 1 && "s"}.</p>
                </div>
                <Button onClick={openCreate} className="text-white h-11" style={{ backgroundColor: "#15803D" }} data-testid="customers-add-btn">
                    <Plus size={16} weight="bold" className="mr-1.5" /> New Customer
                </Button>
            </div>

            <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input placeholder="Search name, mobile, email..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-11" data-testid="customers-search-input" />
            </div>

            <div className="bg-white border border-neutral-200 rounded-md overflow-hidden" data-testid="customers-table">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                            <tr>
                                <th className="text-left px-5 py-3 font-semibold">Name</th>
                                <th className="text-left px-5 py-3 font-semibold">Mobile</th>
                                <th className="text-left px-5 py-3 font-semibold">Email</th>
                                <th className="text-left px-5 py-3 font-semibold">GST</th>
                                <th className="text-right px-5 py-3 font-semibold">Outstanding</th>
                                <th className="text-left px-5 py-3 font-semibold">Added</th>
                                <th className="px-5 py-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={7} className="text-center py-10 text-neutral-400">Loading...</td></tr>}
                            {!loading && items.length === 0 && (
                                <tr><td colSpan={7} className="text-center py-14 text-neutral-400">
                                    <Users size={30} weight="thin" className="mx-auto mb-2 text-neutral-300" />
                                    No customers yet.
                                </td></tr>
                            )}
                            {items.map((c) => (
                                <tr key={c.id} className="data-row border-t border-neutral-100" data-testid={`customer-row-${c.name}`}>
                                    <td className="px-5 py-3 font-semibold">
                                        <button onClick={() => setDrawerId(c.id)} className="hover:underline text-left" data-testid={`customer-view-${c.name}`}>{c.name}</button>
                                    </td>
                                    <td className="px-5 py-3 text-neutral-600">{c.mobile || "-"}</td>
                                    <td className="px-5 py-3 text-neutral-600">{c.email || "-"}</td>
                                    <td className="px-5 py-3 text-xs font-mono text-neutral-500">{c.gst_number || "-"}</td>
                                    <td className="px-5 py-3 text-right font-semibold tabular"
                                        style={{ color: c.outstanding_balance > 0 ? "#DC2626" : "#525252" }}>
                                        {formatCurrency(c.outstanding_balance)}
                                    </td>
                                    <td className="px-5 py-3 text-xs text-neutral-500 whitespace-nowrap">{formatDate(c.created_at)}</td>
                                    <td className="px-5 py-3 text-right whitespace-nowrap">
                                        <button onClick={() => setPaymentCustomerId(c.id)}
                                                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded mr-1"
                                                style={{ background: "#DCFCE7", color: "#15803D" }}
                                                data-testid={`customer-pay-${c.name}`}>
                                            <Coins size={11} /> Payment
                                        </button>
                                        {c.outstanding_balance > 0 && (
                                            <button onClick={() => sendReminder(c)}
                                                    className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded mr-1"
                                                    style={{ background: "#F0FDF4", color: "#15803D" }}
                                                    data-testid={`customer-remind-${c.name}`}>
                                                <WhatsappLogo size={11} weight="fill" style={{ color: "#25D366" }} /> Remind
                                            </button>
                                        )}
                                        <button onClick={() => openEdit(c)} className="text-neutral-500 hover:text-neutral-900" data-testid={`customer-edit-${c.name}`}>
                                            <Pencil size={14} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid="customer-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-extrabold font-display">{editing ? "Edit Customer" : "New Customer"}</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="Name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-form-name" /></Field>
                        <Field label="Mobile"><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="customer-form-mobile" /></Field>
                        <Field label="WhatsApp"><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} data-testid="customer-form-whatsapp" /></Field>
                        <Field label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="customer-form-email" /></Field>
                        <Field label="GST Number"><Input value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value })} data-testid="customer-form-gst" /></Field>
                        <Field label="Credit Limit (₹)"><Input type="number" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} data-testid="customer-form-credit" /></Field>
                    </div>
                    <Field label="Address"><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} data-testid="customer-form-address" /></Field>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="customer-form-cancel">Cancel</Button>
                        <Button onClick={submit} className="text-white" style={{ backgroundColor: "#15803D" }} data-testid="customer-form-submit">
                            {editing ? "Save Changes" : "Create"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <CustomerDetailDrawer
                customerId={drawerId}
                open={!!drawerId}
                onOpenChange={(v) => !v && setDrawerId(null)}
                onUpdate={load}
            />
            <ReceivePaymentDialog
                open={!!paymentCustomerId}
                onOpenChange={(v) => !v && setPaymentCustomerId(null)}
                defaultCustomerId={paymentCustomerId || ""}
                onSaved={() => { load(); setPaymentCustomerId(null); }}
            />
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-neutral-600 mb-1.5 block">{label}</Label>
            {children}
        </div>
    );
}
